import * as React from 'react';
import { PropsWithChildren, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTexture } from '@react-three/drei';
import { ThreeElements, useFrame, useLoader, useThree } from '@react-three/fiber';
import { Color, Matrix4, Quaternion, Texture, Vector3 } from 'three';
import { Font, FontLoader } from 'three-stdlib';

import { SDFTextProviderContext } from './context';
import MSDFTextGeometry from './MSDFTextGeometry';
import { registerTextEngineToR3F } from './register';
import { ISDFFont, ISDFTextInstance, ISDFTextServices, ISDFTextStyle } from './types';

interface ISDFTextStyleGroup {
  textStyle: ISDFTextStyle;
  instances: ISDFTextInstance[];
  transformsDirty: boolean;
}

const temp = new Matrix4();
const transformFinal = new Matrix4();
const textScaleTransform = new Matrix4().makeScale(0.01, 0.01, 0.01);
const textFlipTransform = new Matrix4().makeRotationX(Math.PI);
const white = new Color('#ffffff');
const black = new Color('#000000');

const cameraQ = new Quaternion();
const billboardQ = new Quaternion();
const objectQ = new Quaternion();
const garbage = new Vector3();

function StyleGroupRenderer({
  styleGroup,
  atlas,
  font,
  atlasBold,
  fontBold,
  faceCamera,
  depthTest,
  outlineOpacityMultiplier,
  visible = true,
  ...rest
}: {
  styleGroup: ISDFTextStyleGroup;
  atlas: Texture;
  font: Font;
  atlasBold?: Texture;
  fontBold?: Font;
  faceCamera?: boolean;
  depthTest?: boolean;
  outlineOpacityMultiplier: number;
  visible?: boolean;
} & ThreeElements['mesh']) {
  const invalidate = useThree((state) => state.invalidate);
  const previousCameraQuaternionRef = useRef<Quaternion | null>(null);
  const { instances, textStyle } = styleGroup;

  const shouldDoBold = atlasBold && fontBold && textStyle.bold;
  const fontData = shouldDoBold ? fontBold.data : font.data;

  const geometry = useState(() => new MSDFTextGeometry({ font: fontData, texts: [] }))[0];
  useLayoutEffect(() => () => geometry.dispose(), [geometry]);

  useLayoutEffect(() => {
    const texts = instances.map((is) => is.config);
    geometry.update({ font: fontData, texts });
    styleGroup.transformsDirty = true;
    invalidate();
  }, [instances, geometry, fontData, invalidate, styleGroup]);

  useLayoutEffect(() => {
    if (!visible) return;

    styleGroup.transformsDirty = true;
    invalidate();
  }, [faceCamera, invalidate, styleGroup, visible]);

  useFrame(({ camera }) => {
    if (!visible) return;

    let cameraChanged = false;
    if (faceCamera) {
      camera.getWorldQuaternion(cameraQ);
      cameraChanged = previousCameraQuaternionRef.current?.equals(cameraQ) !== true;
      (previousCameraQuaternionRef.current ??= new Quaternion()).copy(cameraQ);
    }

    if (!styleGroup.transformsDirty && !cameraChanged) return;

    for (let index = 0; index < instances.length; index++) {
      const inst = instances[index];

      transformFinal.copy(inst.transform);
      transformFinal.multiply(textScaleTransform);

      if (faceCamera) {
        transformFinal.decompose(garbage, objectQ, garbage);
        billboardQ.copy(cameraQ).premultiply(objectQ.invert());
        temp.makeRotationFromQuaternion(billboardQ);
        transformFinal.multiply(temp);
      }

      const layout = geometry._layouts[index];
      if (layout) {
        const width = layout.width ?? 0;
        const height = layout.height ?? 0;
        const lineHeight = layout.lineHeight ?? 0;
        const optYShift = layout._options.yShift ?? 0;
        const bottomShift = -height + lineHeight / 4;
        const topShift = lineHeight / 2;
        let xShift = 0;
        if (layout._options.alignX === 'center') xShift = -width / 2;
        if (layout._options.alignX === 'right') xShift = -width;
        let yShift = bottomShift + optYShift;
        if (layout._options.alignY === 'middle') yShift = (bottomShift + topShift) / 2 - layout.ascender + optYShift;
        if (layout._options.alignY === 'bottom') yShift = topShift + optYShift;
        temp.makeTranslation(xShift, yShift, 0);
        transformFinal.multiply(temp);
      }

      transformFinal.multiply(textFlipTransform);

      geometry.setTransform(transformFinal, index);
    }

    geometry.commitTransformUpdate();
    styleGroup.transformsDirty = false;
  });

  const uniforms = useMemo(() => {
    return {
      uMap: { value: shouldDoBold ? atlasBold : atlas },
      uColor: { value: textStyle.color ?? white },
      uOpacity: { value: textStyle.opacity ?? 1.0 },
      uOffset: { value: 0 },
    };
  }, [shouldDoBold, textStyle.opacity, textStyle.color, atlas, atlasBold]);
  const uniformsOutline = useMemo(() => {
    return {
      uMap: { value: shouldDoBold ? atlasBold : atlas },
      uColor: { value: textStyle.outlineColor ?? black },
      uOpacity: { value: (textStyle.outlineOpacity ?? 0.4) * outlineOpacityMultiplier },
      uOffset: { value: textStyle.outlineWidth },
    };
  }, [
    shouldDoBold,
    textStyle.outlineOpacity,
    textStyle.outlineColor,
    textStyle.outlineWidth,
    outlineOpacityMultiplier,
    atlas,
    atlasBold,
  ]);

  return (
    <>
      {(textStyle.outlineWidth ?? 0) > 0 && (
        <mesh geometry={geometry} frustumCulled={false} visible={visible} {...rest}>
          <mSDFTextMaterial uniforms={uniformsOutline} depthTest={depthTest} depthWrite={false} />
        </mesh>
      )}

      <mesh geometry={geometry} frustumCulled={false} visible={visible} {...rest}>
        <mSDFTextMaterial uniforms={uniforms} depthTest={depthTest} />
      </mesh>
    </>
  );
}

// FIXME: since both faceCamera and depthTest are implemented on the style group renderer, it would be possible to
//   move them to `ISDFTextStyle` so they could be set on a per-text basis

export function SDFTextProvider({
  children,
  fontPathRegular,
  fontPathBold,
  faceCamera,
  depthTest,
  visible = true,
  outlineOpacityMultiplier = 1,
}: PropsWithChildren<{
  fontPathRegular: ISDFFont;
  fontPathBold?: ISDFFont;
  faceCamera?: boolean;
  depthTest?: boolean;
  visible?: boolean;
  outlineOpacityMultiplier?: number;
}>) {
  useMemo(registerTextEngineToR3F, []);

  const atlas = useTexture(fontPathRegular.sdfPath);
  const font = useLoader(FontLoader, fontPathRegular.fontPath) as Font;
  const atlasBold = useTexture(fontPathBold ? fontPathBold.sdfPath : []);
  const fontBold = useLoader(FontLoader, fontPathBold ? fontPathBold.fontPath : []) as Font;
  const invalidate = useThree((state) => state.invalidate);

  // We have a mutable map of immutable arrays, so we are using a force update
  const instancesGroups = useState<Map<string, ISDFTextStyleGroup>>(() => new Map())[0];
  const forceUpdate = useState(0)[1];

  const api = useMemo<ISDFTextServices>(
    () => ({
      insertInstance(styleId, textStyle, instanceKey, config) {
        const prevRecord = instancesGroups.get(styleId);
        const newInst: ISDFTextInstance = {
          instanceKey,
          config,
          transform: new Matrix4(),
          setTransform(transform) {
            if (newInst.transform.equals(transform)) return;

            newInst.transform.copy(transform);
            const currentGroup = instancesGroups.get(styleId);
            if (currentGroup) currentGroup.transformsDirty = true;
            invalidate();
          },
        };
        instancesGroups.set(styleId, {
          textStyle,
          instances: [...(prevRecord?.instances ?? []), newInst],
          transformsDirty: true,
        });
        forceUpdate((i) => i + 1);
        return newInst;
      },
      removeInstance(styleId, instanceKey) {
        const prevRecord = instancesGroups.get(styleId);
        if (prevRecord) {
          const without = prevRecord.instances.filter((inst) => inst.instanceKey !== instanceKey);
          if (without.length === 0) {
            instancesGroups.delete(styleId);
          } else {
            instancesGroups.set(styleId, { ...prevRecord, instances: without });
          }
          forceUpdate((i) => i + 1);
        }
      },
    }),
    [forceUpdate, instancesGroups, invalidate],
  );

  return (
    <SDFTextProviderContext.Provider value={api}>
      <>
        {children}

        {Array.from(instancesGroups.entries()).map(([styleId, ig]) => (
          <StyleGroupRenderer
            key={styleId}
            styleGroup={ig}
            atlas={atlas}
            font={font}
            atlasBold={Array.isArray(atlasBold) ? undefined : atlasBold}
            fontBold={Array.isArray(fontBold) ? undefined : fontBold}
            depthTest={depthTest}
            faceCamera={faceCamera}
            outlineOpacityMultiplier={outlineOpacityMultiplier}
            visible={visible}
          />
        ))}
      </>
    </SDFTextProviderContext.Provider>
  );
}
